import {
    ExclamationTriangleIcon,
    ArrowPathIcon,
    ArrowUturnLeftIcon,
    BoltIcon,
    ChevronDownIcon,
    ChevronUpDownIcon,
    ChevronUpIcon,
    ClockIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    StopCircleIcon
} from '@heroicons/react/24/outline'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    useCreateScrapedHistoricFetchMutation,
    useRunScheduledWorkNowMutation,
    useScheduledWorkAttentionCountQuery,
    useFleetJobsQuery,
    useScheduledWorkQuery,
    useStopScheduledWorkMutation
} from '../../api/client'
import PageMasthead from '../../components/PageMasthead'
import { toastError, toastSuccess } from '../../components/Notification'
import HistoricFetchModal from '../../components/scheduledJobs/HistoricFetchModal'
import ScheduledWorkDetailsPanel from '../../components/scheduledJobs/ScheduledWorkDetailsPanel'
import ScheduledWorkHistoryModal from '../../components/scheduledJobs/ScheduledWorkHistoryModal'
import { initialPanelHeight, storePanelHeight } from '../../components/scheduledJobs/panel-height'
import { readSelectedTag, withSelectedTag } from '../../components/scheduledJobs/selected-tag'
import { statusChipClasses } from '../../components/scheduledJobs/status-chip'
import StopScheduledWorkModal from '../../components/scheduledJobs/StopScheduledWorkModal'
import {
    SCRAPE_COMMENT_FETCH_KIND,
    isRecurringScrapeCommentFetchTag
} from '../../components/scheduledJobs/tags'
import type { WorkRow } from '../../components/scheduledJobs/work-row'
import { matchesSearch } from '../../components/scheduledJobs/work-search'

/**
 * The row statuses the scheduler stores, plus "All" for the filter.
 */
const STATUS_FILTERS = [
    'All',
    'Pending',
    'Running',
    'Completed',
    'Failed',
    'Panicked',
    'Cancelled',
    'Deferred'
] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

/**
 * Which jobs the list shows. Fleet jobs cover every account on a platform in
 * one job; account jobs are everything else, such as Monitors.
 */
const JOB_FILTERS = ['all', 'account', 'fleet'] as const
type JobFilter = (typeof JOB_FILTERS)[number]
const JOB_FILTER_LABELS: Record<JobFilter, string> = {
    all: 'All jobs',
    account: 'Account jobs',
    fleet: 'Fleet jobs'
}

/** The scheduler's listing is capped at 500; ask for all of them. */
const WORK_LIMIT = 500

/**
 * How often the listing may refresh itself, via Apollo's own polling.
 * Seconds because that is what the control offers; milliseconds is what
 * Apollo takes.
 */
const REFRESH_SECONDS = [5, 10, 30, 60] as const
type RefreshSeconds = (typeof REFRESH_SECONDS)[number]

const DEFAULT_REFRESH_SECONDS: RefreshSeconds = 10

/**
 * Remembered per browser, not in the URL: how fast you like the page to
 * tick is a preference, unlike the search and filters beside it, which
 * describe what you are looking at and are worth sharing in a link.
 */
const REFRESH_STORAGE_KEY = 'scheduledJobs.refreshSeconds'

function storedRefreshSeconds(): RefreshSeconds {
    const saved = Number(localStorage.getItem(REFRESH_STORAGE_KEY))
    // Anything unrecognised — absent, hand-edited, or an option we have
    // since dropped — falls back rather than polling at some stale rate.
    return (REFRESH_SECONDS as readonly number[]).includes(saved)
        ? (saved as RefreshSeconds)
        : DEFAULT_REFRESH_SECONDS
}

/** The sortable columns, in the order they appear in the table. */
const COLUMNS = [
    { key: 'tag', label: 'Tag' },
    { key: 'kind', label: 'Kind' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'status', label: 'Status' },
    { key: 'nextRun', label: 'Next run' },
    { key: 'latest', label: 'Latest' }
] as const
type SortColumn = (typeof COLUMNS)[number]['key']
type SortState = { column: SortColumn; direction: 'asc' | 'desc' }

/** English description first, then the raw cron, then the interval. */
const scheduleText = (work: WorkRow) => {
    if (work.scheduleEnglish) return work.scheduleEnglish
    if (work.cron) return work.cron
    if (work.intervalSecs != null) return `every ${work.intervalSecs}s`
    // No schedule at all. On work that has ended, that is also the reason.
    return work.live ? 'one-off' : 'ended'
}

/**
 * What a column sorts on. `null` means the row has no value for it and
 * belongs at the end regardless of direction.
 *
 * Strings are lowercased so sorting matches what the eye expects rather than
 * ASCII, which would put every capital ahead of every lowercase letter.
 */
const sortValue = (row: WorkRow, column: SortColumn): string | number | null => {
    switch (column) {
        case 'tag':
            return row.tag.toLowerCase()
        case 'kind':
            return row.kind.toLowerCase()
        // Sorted by the text on screen, so the order matches what is read.
        case 'schedule':
            return scheduleText(row).toLowerCase()
        case 'status':
            return row.status.toLowerCase()
        // By instant, not by the "in 4 days" wording, which would sort
        // alphabetically and be nonsense.
        case 'nextRun':
            return row.nextRunAt ? new Date(row.nextRunAt).getTime() : null
        case 'latest':
            return latestText(row)?.toLowerCase() ?? null
    }
}

/**
 * What the Latest column says: the last FINISHED run's words.
 */
const latestText = (row: WorkRow) => row.lastRunLog ?? row.log ?? null

const renderNextRun = (value?: string | null) => {
    if (!value) return <span className="text-ink-400">—</span>
    try {
        const d = parseISO(value)
        return (
            <div className="text-sm text-ink-900">
                {formatDistanceToNow(d, { addSuffix: true })}
                <div className="text-xs text-ink-500">{d.toLocaleString()}</div>
            </div>
        )
    } catch {
        return <span className="text-ink-500">{value}</span>
    }
}

/**
 * Admin listing of the Shino scheduler's work: monitoring schedules and the
 * service's own housekeeping alike. One line per tag, showing that work's
 * newest row, so work that has ended stays visible with the row that ended
 * it rather than disappearing.
 *
 * Row actions: read the history, fetch history for a monitored account, bring
 * the next run forward, and stop. Stop sits behind a confirm because there is
 * no resume to undo it with, and so does historic fetch, because it spends real
 * scraping budget in one go.
 */
function ScheduledJobsPage() {
    const navigate = useNavigate()
    const [workToStop, setWorkToStop] = useState<WorkRow | null>(null)
    const [historyTag, setHistoryTag] = useState<string | null>(null)
    const [historicFetchTag, setHistoricFetchTag] = useState<string | null>(null)
    // Search, status filter and the attention toggle all live in the URL, so
    // a view — "failing only", "Cancelled rows matching acme" — can be linked
    // to a colleague and survives a refresh.
    const [searchParams, setSearchParams] = useSearchParams()
    const search = searchParams.get('q') ?? ''
    const statusParam = searchParams.get('status')
    const statusFilter: StatusFilter = (STATUS_FILTERS as readonly string[]).includes(
        statusParam ?? ''
    )
        ? (statusParam as StatusFilter)
        : 'All'
    const attentionOnly = searchParams.get('attention') === '1'
    const jobsParam = searchParams.get('jobs')
    const jobFilter: JobFilter = (JOB_FILTERS as readonly string[]).includes(jobsParam ?? '')
        ? (jobsParam as JobFilter)
        : 'all'
    // The selected row is part of "what am I looking at", like the filters,
    // so it lives in the URL too and a link opens the page with the panel up.
    const selectedTag = readSelectedTag(searchParams)
    const setSelectedTag = (tag: string | null) =>
        setSearchParams(withSelectedTag(searchParams, tag), { replace: true })
    const updateParams = (mutate: (params: URLSearchParams) => void) => {
        const params = new URLSearchParams(searchParams)
        mutate(params)
        setSearchParams(params, { replace: true })
    }
    const setSearch = (next: string) =>
        updateParams((params) => (next ? params.set('q', next) : params.delete('q')))
    const setJobFilter = (next: JobFilter) =>
        updateParams((params) =>
            next !== 'all' ? params.set('jobs', next) : params.delete('jobs')
        )
    const setStatusFilter = (next: StatusFilter) =>
        updateParams((params) =>
            next !== 'All' ? params.set('status', next) : params.delete('status')
        )
    // Turning the attention toggle on clears the status filter: the failing
    // rows are live (Pending/Running), so any terminal status filter would
    // hide exactly what was asked for.
    const toggleAttention = () =>
        updateParams((params) => {
            if (attentionOnly) {
                params.delete('attention')
            } else {
                params.set('attention', '1')
                params.delete('status')
            }
        })
    // Tag ascending by default: the tags are account-prefixed, so this groups
    // each account's monitoring and its historic fetches together on sight.
    const [sort, setSort] = useState<SortState>({ column: 'tag', direction: 'asc' })

    /** Click a heading to sort by it; click the same one again to reverse. */
    const toggleSort = (column: SortColumn) =>
        setSort((current) =>
            current.column === column
                ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { column, direction: 'asc' }
        )

    const [refreshSeconds, setRefreshSeconds] = useState<RefreshSeconds>(storedRefreshSeconds)
    useEffect(() => {
        localStorage.setItem(REFRESH_STORAGE_KEY, String(refreshSeconds))
    }, [refreshSeconds])
    const pollInterval = refreshSeconds * 1000

    // How tall the details panel opens. A preference like the refresh rate,
    // so it is remembered per browser rather than carried in the URL.
    const [panelHeight, setPanelHeight] = useState(() => initialPanelHeight(window.innerHeight))
    useEffect(() => {
        storePanelHeight(panelHeight)
    }, [panelHeight])

    const { data, loading, error, refetch } = useScheduledWorkQuery({
        variables: {
            status: statusFilter === 'All' ? undefined : statusFilter,
            limit: WORK_LIMIT
        },
        // Always revalidate on mount so the list is fresh after returning
        // from the create page (which lives on its own route, so
        // refetchQueries can't reach an unmounted query).
        fetchPolicy: 'cache-and-network',
        pollInterval
    })

    // The details panel fetches its Recent runs once per selected row rather
    // than polling the whole history, so the run a Stop or Run now creates
    // or cancels only shows there if the action refetches it.
    const [stopWork, { loading: stopping }] = useStopScheduledWorkMutation({
        refetchQueries: ['scheduledWorkHistory']
    })
    const [runNow, { loading: running }] = useRunScheduledWorkNowMutation({
        refetchQueries: ['scheduledWorkHistory']
    })
    const [createScrapedHistoricFetch, { loading: fetchingHistory }] =
        useCreateScrapedHistoricFetchMutation()
    const actionInFlight = stopping || running || fetchingHistory

    // Memoised so the sort/filter memo below has a stable dependency (this
    // was the build's one standing eslint warning).
    const work: WorkRow[] = useMemo(() => data?.scheduledWork ?? [], [data])

    // The same query the sidebar badge polls, so the pill and the badge can
    // never disagree — the list fetch is status-filtered, so counting rows
    // here would undercount whenever a status filter is active.
    const { data: attentionData, refetch: refetchAttention } = useScheduledWorkAttentionCountQuery({
        pollInterval,
        errorPolicy: 'ignore'
    })
    const attentionCount = attentionData?.scheduledWorkAttentionCount ?? 0

    /**
     * Search and sort are client-side, on purpose.
     *
     * The query already fetches every piece of work up to the scheduler's own
     * 500 ceiling, so filtering and ordering in the browser is instant and
     * needs no API surface. Status stays server-side because it is a query
     * argument the scheduler already understands, and pushing it down keeps
     * the fetched set smaller.
     *
     * If the ceiling is ever raised past what a page can reasonably hold,
     * these two move to the resolver and the sort key goes into the SQL.
     */
    // Fleet jobs are a fixed list, so this is fetched once rather than polled.
    const { data: fleetData } = useFleetJobsQuery({ fetchPolicy: 'cache-first' })
    const fleetTags = useMemo(
        () => new Set((fleetData?.fleetJobs ?? []).map((job) => job.tag)),
        [fleetData]
    )

    const visible = useMemo(() => {
        let matched = work.filter((row) => matchesSearch(row, search))
        if (jobFilter !== 'all') {
            matched = matched.filter((row) => fleetTags.has(row.tag) === (jobFilter === 'fleet'))
        }
        if (attentionOnly) {
            matched = matched.filter((row) => row.needsAttention)
        }

        const direction = sort.direction === 'asc' ? 1 : -1
        // Copy first: the query result is frozen by Apollo's cache.
        // Failing-live work sorts ahead of everything, whatever column is
        // chosen: this page exists to answer "what needs me?", so the
        // answer goes first and the chosen sort orders each half.
        return [...matched].sort((a, b) => {
            if (a.needsAttention !== b.needsAttention) {
                return a.needsAttention ? -1 : 1
            }
            const left = sortValue(a, sort.column)
            const right = sortValue(b, sort.column)
            // Absent values sort last whichever way the column is pointing —
            // "no next run" is not a small date, it is the absence of one.
            if (left === null && right === null) return 0
            if (left === null) return 1
            if (right === null) return -1
            if (left < right) return -1 * direction
            if (left > right) return 1 * direction
            // Tag breaks every tie, so the order is stable between polls.
            return a.tag.localeCompare(b.tag)
        })
    }, [work, search, sort, attentionOnly, jobFilter, fleetTags])

    // Looked up in the full list, not the visible one: a selected row that
    // the search has filtered out should still show its details rather than
    // vanish, and the panel says when the tag is not in the list at all.
    const selectedRow = useMemo(
        () => (selectedTag ? (work.find((row) => row.tag === selectedTag) ?? null) : null),
        [work, selectedTag]
    )

    /**
     * Both lifecycle mutations return the same honest shape: `message` is
     * ready for a toast, `changed` says whether the list needs refreshing
     * ("already stopped" changes nothing).
     */
    const runAction = async (
        run: () => Promise<{ changed: boolean; message: string } | undefined>
    ) => {
        try {
            const result = await run()
            if (result) {
                toastSuccess(result.message)
                if (result.changed) refetch()
            }
        } catch (err: any) {
            toastError(err.message)
        }
    }

    const handleRunNow = (row: WorkRow) =>
        runAction(async () => {
            const result = await runNow({ variables: { tag: row.tag } })
            return result.data?.runScheduledWorkNow
        })

    const confirmHistoricFetch = async (since: string, scrapeProfile: string) => {
        if (!historicFetchTag) return
        try {
            const result = await createScrapedHistoricFetch({
                variables: { monitorTag: historicFetchTag, since, scrapeProfile }
            })
            const outcome = result.data?.createScrapedHistoricFetch
            if (outcome) {
                // The message already distinguishes "queued" from "one
                // already covered this range", so it can be shown as-is.
                toastSuccess(outcome.message)
                refetch()
            }
            setHistoricFetchTag(null)
        } catch (err: any) {
            toastError(err.message)
        }
    }

    const confirmStop = async () => {
        if (!workToStop) return
        await runAction(async () => {
            const result = await stopWork({ variables: { tag: workToStop.tag } })
            return result.data?.stopScheduledWork
        })
        setWorkToStop(null)
    }

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-ink-50 flex items-center justify-center">
                <div className="text-ink-600">Loading scheduled jobs...</div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen bg-ink-50 flex items-center justify-center">
                <div className="text-severe-600">Error loading scheduled jobs: {error.message}</div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead
                title="Scheduled jobs"
                tabTitle="Scheduled jobs"
                // Says both numbers while searching, so a short list reads as
                // "filtered" rather than as "most of them have vanished".
                subtitle={
                    (search.trim() || jobFilter !== 'all') && visible.length !== work.length
                        ? `${visible.length} of ${work.length} schedules`
                        : `${work.length} schedule${work.length === 1 ? '' : 's'}`
                }
                actions={
                    <div className="flex items-center space-x-3">
                        {/* Both queries, so the pill and the list cannot
                            disagree after a manual refresh. */}
                        <button
                            type="button"
                            onClick={() => {
                                refetch()
                                refetchAttention()
                            }}
                            disabled={loading}
                            title="Refresh now"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm font-medium border border-ink-300 text-ink-700 bg-white hover:bg-ink-50 disabled:opacity-50"
                        >
                            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                        <label htmlFor="refresh-interval" className="sr-only">
                            Refresh every
                        </label>
                        <select
                            id="refresh-interval"
                            value={refreshSeconds}
                            onChange={(e) =>
                                setRefreshSeconds(Number(e.target.value) as RefreshSeconds)
                            }
                            title="How often this page refreshes itself"
                            className="block border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500"
                        >
                            {REFRESH_SECONDS.map((seconds) => (
                                <option key={seconds} value={seconds}>
                                    Every {seconds}s
                                </option>
                            ))}
                        </select>
                        {attentionCount > 0 || attentionOnly ? (
                            <button
                                type="button"
                                onClick={toggleAttention}
                                aria-pressed={attentionOnly}
                                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm font-medium border ${
                                    attentionOnly
                                        ? 'bg-severe-50 border-severe-300 text-severe-800'
                                        : 'bg-white border-ink-300 text-severe-700 hover:bg-severe-50'
                                }`}
                                title="Live schedules whose last run failed"
                            >
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                {attentionCount} failing
                            </button>
                        ) : null}
                        <label htmlFor="work-search" className="sr-only">
                            Search by tag, kind or summary
                        </label>
                        <div className="relative">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                            <input
                                id="work-search"
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search tag, kind or summary"
                                className="block w-56 pl-8 border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500"
                            />
                        </div>
                        <label htmlFor="job-filter" className="sr-only">
                            Filter by job type
                        </label>
                        <select
                            id="job-filter"
                            value={jobFilter}
                            onChange={(e) => setJobFilter(e.target.value as JobFilter)}
                            className="block border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500"
                        >
                            {JOB_FILTERS.map((filter) => (
                                <option key={filter} value={filter}>
                                    {JOB_FILTER_LABELS[filter]}
                                </option>
                            ))}
                        </select>
                        <label htmlFor="status-filter" className="sr-only">
                            Filter by status
                        </label>
                        <select
                            id="status-filter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                            className="block border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500"
                        >
                            {STATUS_FILTERS.map((status) => (
                                <option key={status} value={status}>
                                    {status === 'All' ? 'All statuses' : status}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => navigate('/admin/scheduled-jobs/new')}
                            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-arwen-600 hover:bg-arwen-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500"
                        >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add schedule
                        </button>
                    </div>
                }
            />

            <p className="px-6 pt-4 text-sm text-ink-500">
                Recurring work run by the Shino scheduler: account jobs such as monitoring
                schedules, and fleet jobs, which each cover every account on a platform. Stopping an
                account job has no undo; to run it again, create its schedule afresh. A stopped
                fleet job comes back on the next deploy.
            </p>

            <div className="flex flex-col my-2">
                {/* overflow-x-auto rather than overflow-hidden: whatever width
                    is left over after the wrapping above scrolls inside this
                    card, instead of clipping or dragging the whole page
                    sideways. */}
                {/* While the panel is docked, leave exactly its height below
                    the table so the last rows can still scroll clear of it. */}
                <div
                    className="bg-white shadow rounded-lg overflow-x-auto"
                    style={selectedTag ? { marginBottom: panelHeight } : undefined}
                >
                    <table className="min-w-full divide-y divide-ink-200">
                        <thead className="bg-ink-50">
                            <tr>
                                {COLUMNS.map((column) => (
                                    <th
                                        key={column.key}
                                        className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider"
                                        aria-sort={
                                            sort.column === column.key
                                                ? sort.direction === 'asc'
                                                    ? 'ascending'
                                                    : 'descending'
                                                : 'none'
                                        }
                                    >
                                        <button
                                            onClick={() => toggleSort(column.key)}
                                            className="group inline-flex items-center uppercase tracking-wider hover:text-ink-700"
                                            title={`Sort by ${column.label.toLowerCase()}`}
                                        >
                                            {column.label}
                                            {/* The arrow only shows on the
                                                active column, and faintly on
                                                hover elsewhere, so the header
                                                stays quiet while still saying
                                                every column is sortable. */}
                                            {sort.column === column.key ? (
                                                sort.direction === 'asc' ? (
                                                    <ChevronUpIcon className="ml-1 h-3 w-3" />
                                                ) : (
                                                    <ChevronDownIcon className="ml-1 h-3 w-3" />
                                                )
                                            ) : (
                                                <ChevronUpDownIcon className="ml-1 h-3 w-3 text-ink-300 group-hover:text-ink-400" />
                                            )}
                                        </button>
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-ink-200">
                            {visible.length === 0 && work.length > 0 ? (
                                <tr>
                                    <td
                                        colSpan={COLUMNS.length + 1}
                                        className="px-4 py-8 text-center text-sm text-ink-500"
                                    >
                                        {search.trim()
                                            ? `Nothing matches “${search}”. Searching looks at the tag, the kind and the summary.`
                                            : `No ${JOB_FILTER_LABELS[jobFilter].toLowerCase()} match these filters.`}
                                    </td>
                                </tr>
                            ) : null}
                            {visible.map((row) => (
                                <tr
                                    key={row.tag}
                                    onClick={() => setSelectedTag(row.tag)}
                                    aria-current={row.tag === selectedTag ? 'true' : undefined}
                                    className={`cursor-pointer ${
                                        row.tag === selectedTag ? 'bg-arwen-50' : 'hover:bg-ink-50'
                                    }`}
                                >
                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                        <button
                                            type="button"
                                            className="text-left"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                setSelectedTag(row.tag)
                                            }}
                                        >
                                            <span className="block text-sm font-medium text-ink-900">
                                                {row.tag}
                                            </span>
                                            <span
                                                className="block text-xs text-ink-500"
                                                title={row.summary}
                                            >
                                                {row.summary}
                                            </span>
                                        </button>
                                    </td>
                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                        <div className="text-sm text-ink-500">{row.kind}</div>
                                    </td>
                                    {/* Deliberately NOT nowrap, and capped: a
                                        windowed cron describes itself in about
                                        a hundred characters ("Every 2 minutes,
                                        between 5:00 PM and 6:00 PM, on day 31
                                        of the month..."), which on one line
                                        sets the width of the whole table. */}
                                    <td className="px-4 py-2.5 max-w-sm">
                                        <div className="text-sm text-ink-900">
                                            {scheduleText(row)}
                                        </div>
                                        {row.scheduleEnglish && row.cron ? (
                                            <div className="text-xs text-ink-500 font-mono">
                                                {row.cron}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusChipClasses(
                                                row.status
                                            )}`}
                                        >
                                            {row.status}
                                        </span>
                                        {/* A retry means an earlier attempt
                                            failed, and its reason is on THAT
                                            row, not this one — so this badge
                                            has to send you to the history
                                            rather than just noting the number. */}
                                        {row.attempt > 1 ? (
                                            <button
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    setHistoryTag(row.tag)
                                                }}
                                                className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-suspect-700 bg-suspect-100 hover:bg-suspect-200"
                                                title="An earlier attempt failed — open the history for the reason"
                                            >
                                                try {row.attempt}
                                            </button>
                                        ) : null}
                                    </td>
                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                        {renderNextRun(row.nextRunAt)}
                                    </td>
                                    <td className="px-4 py-2.5 max-w-xs">
                                        {row.needsAttention ? (
                                            <div
                                                className="text-sm text-severe-700 truncate"
                                                title={latestText(row) ?? undefined}
                                            >
                                                <ExclamationTriangleIcon className="inline h-4 w-4 mr-1 align-text-bottom" />
                                                {row.consecutiveFailures > 1
                                                    ? `Failed ×${row.consecutiveFailures} — `
                                                    : ''}
                                                {latestText(row) ?? 'last run failed'}
                                            </div>
                                        ) : latestText(row) ? (
                                            <div
                                                className="text-sm text-ink-500 truncate"
                                                title={latestText(row) ?? undefined}
                                            >
                                                {latestText(row)}
                                            </div>
                                        ) : (
                                            <span className="text-ink-400">—</span>
                                        )}
                                    </td>
                                    <td
                                        className="px-4 py-2.5 whitespace-nowrap text-right text-sm font-medium space-x-3"
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        {/* History is offered on every row,
                                            live or ended: work that has
                                            finished is exactly the work whose
                                            failures you want to read. */}
                                        <button
                                            onClick={() => setHistoryTag(row.tag)}
                                            className="inline-flex items-center"
                                            title="History — every run, and why each one ended"
                                            aria-label={`History for ${row.tag}`}
                                        >
                                            <ClockIcon className="h-5 w-5 text-ink-400 hover:text-ink-600" />
                                        </button>
                                        {/* Historic fetch is offered on Monitors only: the
                                            backend builds the historic run from
                                            the monitor's own params, and a
                                            heartbeat has none to read. Offered on
                                            ended monitoring too — wanting the
                                            history of an account you have
                                            stopped watching is reasonable. The kind
                                            check is belt and braces: the tag's
                                            third segment is the kind, but the kind
                                            column is what the backend routes on. */}
                                        {row.kind === SCRAPE_COMMENT_FETCH_KIND &&
                                        isRecurringScrapeCommentFetchTag(row.tag) ? (
                                            <button
                                                onClick={() => setHistoricFetchTag(row.tag)}
                                                className="inline-flex items-center"
                                                title="Historic fetch — one run, back to a date you choose"
                                                aria-label={`Historic fetch for ${row.tag}`}
                                                disabled={actionInFlight}
                                            >
                                                <ArrowUturnLeftIcon className="h-5 w-5 text-ink-400 hover:text-arwen-600" />
                                            </button>
                                        ) : null}
                                        {row.live ? (
                                            <>
                                                <button
                                                    onClick={() => handleRunNow(row)}
                                                    className="inline-flex items-center"
                                                    title="Run now"
                                                    aria-label={`Run ${row.tag} now`}
                                                    disabled={actionInFlight}
                                                >
                                                    <BoltIcon className="h-5 w-5 text-arwen-500 hover:text-arwen-700" />
                                                </button>
                                                <button
                                                    onClick={() => setWorkToStop(row)}
                                                    className="inline-flex items-center"
                                                    title="Stop (no undo)"
                                                    aria-label={`Stop ${row.tag}`}
                                                    disabled={actionInFlight}
                                                >
                                                    <StopCircleIcon className="h-5 w-5 text-severe-500 hover:text-severe-700" />
                                                </button>
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {work.length === 0 ? (
                        <div className="p-6 text-center text-sm text-ink-500">
                            <ExclamationTriangleIcon className="h-6 w-6 text-ink-400 mx-auto mb-2" />
                            No scheduled jobs
                            {statusFilter === 'All' ? '' : ` with status ${statusFilter}`}. Use
                            &ldquo;Add schedule&rdquo; to create one.
                        </div>
                    ) : null}
                </div>
            </div>

            {selectedTag ? (
                <ScheduledWorkDetailsPanel
                    tag={selectedTag}
                    row={selectedRow}
                    actionInFlight={actionInFlight}
                    escapeCloses={
                        historyTag === null && historicFetchTag === null && workToStop === null
                    }
                    height={panelHeight}
                    onHeightChange={setPanelHeight}
                    onClose={() => setSelectedTag(null)}
                    onHistory={setHistoryTag}
                    onHistoricFetch={setHistoricFetchTag}
                    onRunNow={handleRunNow}
                    onStop={setWorkToStop}
                />
            ) : null}

            <ScheduledWorkHistoryModal tag={historyTag} onClose={() => setHistoryTag(null)} />

            <HistoricFetchModal
                monitorTag={historicFetchTag}
                onClose={() => setHistoricFetchTag(null)}
                onConfirm={confirmHistoricFetch}
                loading={fetchingHistory}
            />

            <StopScheduledWorkModal
                open={workToStop !== null}
                tag={workToStop?.tag ?? ''}
                onClose={() => setWorkToStop(null)}
                onConfirm={confirmStop}
                loading={stopping}
            />
        </div>
    )
}

export default ScheduledJobsPage
